/**
 * consoleCapture — a tiny ring buffer that records recent console output so it
 * can be attached to diagnostics (e.g. the SlowRequestPrompt support ticket).
 *
 * The browser does NOT let us read what was already printed to the devtools
 * console, so we patch `console.*` once, early in app startup, and keep the last
 * N entries in memory. Only output produced AFTER `installConsoleCapture()` runs
 * is captured — install it as early as possible (see src/index.js).
 *
 * Patching is idempotent and transparent: every call is still forwarded to the
 * original console method, so devtools behave exactly as before.
 */

const MAX_ENTRIES = 150;

// Ring buffer of { t: epochMs, level, text }.
const buffer = [];
let installed = false;

const safeStringify = (arg) => {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (arg === null) return "null";
  if (arg === undefined) return "undefined";
  try {
    const seen = new WeakSet();
    return JSON.stringify(arg, (k, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      if (typeof v === "function") return `[fn ${v.name || "anonymous"}]`;
      return v;
    });
  } catch {
    try {
      return String(arg);
    } catch {
      return "[unserializable]";
    }
  }
};

const record = (level, args) => {
  let text;
  try {
    text = Array.from(args).map(safeStringify).join(" ");
  } catch {
    text = "[unrecordable log entry]";
  }
  // Cap very long lines so one giant payload can't dominate the buffer.
  if (text.length > 2000) text = `${text.slice(0, 2000)}… [truncated]`;
  buffer.push({ t: Date.now(), level, text });
  if (buffer.length > MAX_ENTRIES) buffer.shift();
};

export const installConsoleCapture = () => {
  if (installed || typeof console === "undefined") return;
  installed = true;
  ["log", "info", "warn", "error", "debug"].forEach((level) => {
    const original = console[level];
    if (typeof original !== "function") return;
    console[level] = (...args) => {
      record(level, args);
      original.apply(console, args);
    };
  });

  // Also capture uncaught errors and unhandled promise rejections — these are
  // usually the most useful thing in a "it just hung / broke" report.
  if (typeof window !== "undefined") {
    window.addEventListener("error", (e) => {
      record("error", [
        `[window.onerror] ${e?.message || ""}`,
        e?.filename ? `${e.filename}:${e.lineno}:${e.colno}` : "",
      ]);
    });
    window.addEventListener("unhandledrejection", (e) => {
      const reason = e?.reason;
      record("error", [
        "[unhandledrejection]",
        reason instanceof Error
          ? `${reason.name}: ${reason.message}`
          : safeStringify(reason),
      ]);
    });
  }
};

/**
 * Returns the most recent captured console entries, newest last, as plain
 * strings like "12:01:03 [error] Something failed".
 * @param {number} limit how many trailing entries to return (default 40)
 */
export const getRecentConsoleLogs = (limit = 40) =>
  buffer.slice(-limit).map(({ t, level, text }) => {
    const time = new Date(t).toLocaleTimeString();
    return `${time} [${level}] ${text}`;
  });

export default installConsoleCapture;
